import { DEFAULT_VEHICLE_LAT, DEFAULT_VEHICLE_LON, DEFAULT_VEHICLE_ALT, type VehicleState } from '@k-ucap/shared';
import type { MessageEvent, MessageInstance } from '@k-ucap/shared';

export interface GisMessageSource {
  id?: string;
  messageType: number;
  projectId?: string;
  environment?: string;
  timestamp: string;
  fields: Record<string, unknown>;
}

export const toGisMessageSource = (event: MessageEvent | MessageInstance): GisMessageSource => ({
  id: event.id,
  messageType: event.messageType,
  projectId: event.projectId,
  environment: event.environment,
  timestamp: event.timestamp,
  fields: event.fields ?? {},
});

export interface TrackPoint {
  id: string;
  vehicleId: string;
  latitude: number;
  longitude: number;
  altitudeM: number;
  timestamp: string;
}

export interface GisAlert {
  id: string;
  vehicleId: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL' | 'UNKNOWN';
  message: string;
  timestamp: string;
  coordinates?: { latitude: number; longitude: number; altitudeM?: number };
  acknowledged: boolean;
  sourceMessageType?: number;
  sourceMessageId?: string;
}

export interface GisWaypointUpdate {
  vehicleId: string;
  fromWaypointId?: string;
  toWaypointId?: string;
  nextWaypointId?: string;
  timestamp: string;
}

export interface GisProjectionUpdate {
  vehicleUpdate?: VehicleState;
  trackPoint?: TrackPoint;
  alert?: GisAlert;
  waypointUpdate?: GisWaypointUpdate;
}

export interface GisProjectionOptions {
  projectId?: string;
  environment?: string;
}

const parseNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const parseString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return undefined;
};

const nowIso = (): string => new Date().toISOString();

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

export class GisMessageProjectionService {
  private projectId?: string;
  private environment?: string;

  constructor(options: GisProjectionOptions = {}) {
    this.projectId = options.projectId;
    this.environment = options.environment;
  }

  processMessageEvent(event: GisMessageSource): GisProjectionUpdate | null {
    const messageType = event.messageType;
    const vehicleId = parseString(event.fields?.vehicleId) ?? 'UNKNOWN';
    const timestamp = event.timestamp ?? nowIso();

    switch (messageType) {
      case 101:
        return this.projectInertialState(event, vehicleId, timestamp);
      case 104:
        return this.projectOperatingState(event, vehicleId, timestamp);
      case 110:
        return this.projectWaypointState(event, vehicleId, timestamp);
      case 1100:
        return this.projectAlert(event, vehicleId, timestamp);
      default:
        return null;
    }
  }

  static project(message: GisMessageSource, vehicleId?: string): GisProjectionUpdate | null {
    const service = new GisMessageProjectionService();
    const event = {
      ...message,
      fields: message.fields ?? {},
      vehicleId: vehicleId ?? parseString(message.fields?.vehicleId) ?? 'UNKNOWN',
    };
    return service.processMessageEvent(event);
  }

  private projectInertialState(
    event: GisMessageSource,
    vehicleId: string,
    timestamp: string
  ): GisProjectionUpdate | null {
    const fields = event.fields ?? {};
    const latitude = parseNumber(fields.latitude);
    const longitude = parseNumber(fields.longitude);
    const altitudeM = parseNumber(fields.altitudeM) ?? parseNumber(fields.altitude) ?? 0;

    if (latitude === undefined || longitude === undefined) {
      return null;
    }

    const yawDeg = parseNumber(fields.yawDeg) ?? parseNumber(fields.headingDeg) ?? 0;
    const northVelocity = parseNumber(fields.northVelocityMps) ?? 0;
    const eastVelocity = parseNumber(fields.eastVelocityMps) ?? 0;
    const speedMps = Math.sqrt(northVelocity * northVelocity + eastVelocity * eastVelocity);

    const vehicleUpdate: VehicleState = {
      id: vehicleId,
      projectId: event.projectId ?? this.projectId ?? 'unknown',
      vehicleId,
      callsign: parseString(fields.callsign) ?? vehicleId,
      coordinates: { latitude, longitude, altitudeM },
      headingDeg: yawDeg,
      speedMps: Number(speedMps.toFixed(2)),
      rollDeg: parseNumber(fields.rollDeg),
      pitchDeg: parseNumber(fields.pitchDeg),
      yawDeg,
      northVelocityMps: northVelocity,
      eastVelocityMps: eastVelocity,
      verticalVelocityMps: parseNumber(fields.verticalVelocityMps),
      status: 'INERTIAL_UPDATE',
      environment: event.environment ?? this.environment ?? 'development',
      updatedAt: timestamp,
    };

    const trackPoint: TrackPoint = {
      id: `${event.id ?? generateId()}-track`,
      vehicleId,
      latitude,
      longitude,
      altitudeM,
      timestamp,
    };

    return { vehicleUpdate, trackPoint };
  }

  private projectOperatingState(
    event: GisMessageSource,
    vehicleId: string,
    timestamp: string
  ): GisProjectionUpdate | null {
    const fields = event.fields ?? {};
    const status = parseString(fields.operatingState) ?? parseString(fields.vehicleState) ?? 'UNKNOWN';
    const latitude = parseNumber(fields.latitude) ?? DEFAULT_VEHICLE_LAT;
    const longitude = parseNumber(fields.longitude) ?? DEFAULT_VEHICLE_LON;
    const altitudeM = parseNumber(fields.altitudeM) ?? parseNumber(fields.altitude) ?? DEFAULT_VEHICLE_ALT;
    const headingDeg = parseNumber(fields.headingDeg) ?? 0;
    const speedMps = parseNumber(fields.speedMps) ?? 0;

    const vehicleUpdate: VehicleState = {
      id: vehicleId,
      projectId: event.projectId ?? this.projectId ?? 'unknown',
      vehicleId,
      callsign: parseString(fields.callsign) ?? vehicleId,
      coordinates: { latitude, longitude, altitudeM },
      headingDeg,
      speedMps,
      status,
      environment: event.environment ?? this.environment ?? 'development',
      updatedAt: timestamp,
    };

    return { vehicleUpdate };
  }

  private projectWaypointState(
    event: GisMessageSource,
    vehicleId: string,
    timestamp: string
  ): GisProjectionUpdate | null {
    const fields = event.fields ?? {};
    const waypointUpdate: GisWaypointUpdate = {
      vehicleId,
      fromWaypointId: parseString(fields.fromWaypointId) ?? parseString(fields.fromWaypoint),
      toWaypointId: parseString(fields.toWaypointId) ?? parseString(fields.toWaypoint),
      nextWaypointId: parseString(fields.nextWaypointId) ?? parseString(fields.nextWaypoint),
      timestamp,
    };

    return { waypointUpdate };
  }

  private projectAlert(event: GisMessageSource, vehicleId: string, timestamp: string): GisProjectionUpdate | null {
    const fields = event.fields ?? {};
    const severityRaw = parseString(fields.alertLevel) ?? parseString(fields.severity) ?? 'INFO';
    const severity = this.normalizeSeverity(severityRaw);
    const message = parseString(fields.description) ?? parseString(fields.message) ?? '알림 메시지';
    const latitude = parseNumber(fields.latitude);
    const longitude = parseNumber(fields.longitude);
    const altitudeM = parseNumber(fields.altitudeM) ?? parseNumber(fields.altitude);

    const alert: GisAlert = {
      id: event.id ?? generateId(),
      vehicleId,
      severity,
      message,
      timestamp,
      coordinates: latitude !== undefined && longitude !== undefined ? { latitude, longitude, altitudeM } : undefined,
      acknowledged: false,
      sourceMessageType: event.messageType,
      sourceMessageId: event.id,
    };

    return { alert };
  }

  private normalizeSeverity(value: string): GisAlert['severity'] {
    const upper = value.toUpperCase();
    if (upper === 'CRITICAL' || upper === 'ERROR' || upper === 'DANGER') return 'CRITICAL';
    if (upper === 'WARNING' || upper === 'WARN') return 'WARNING';
    if (upper === 'INFO' || upper === 'INFORMATION') return 'INFO';
    return 'UNKNOWN';
  }
}

export const createGisMessageProjectionService = (options?: GisProjectionOptions): GisMessageProjectionService =>
  new GisMessageProjectionService(options);
